/**
 * Mutation hooks for list item operations (add, remove, update note)
 *
 * Provides React Query mutations wrapping the list items API endpoints.
 * Each mutation invalidates relevant query keys on success and
 * optimistically updates feed listSaveCount for instant UI feedback.
 */

import * as listsApi from '@/api/endpoints/lists';
import { FEED_QUERY_KEYS } from '@/api/queries/activity';
import { LISTS_QUERY_KEYS } from '@/api/queries/lists';
import type { ListItemCreateInput, ListItemUpdateInput } from '@/api/schemas/list';
import type { FeedItem, PaginatedFeedResponse } from '@/types/feed';
import { useMutation, useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';

/**
 * Optimistically update listSaveCount in all cached feed queries.
 * Follows the same setQueriesData pattern used by use-like-check-in.ts.
 */
function optimisticFeedCountUpdate(
  queryClient: ReturnType<typeof useQueryClient>,
  menuItemId: string,
  delta: 1 | -1,
) {
  const updateItem = (item: FeedItem): FeedItem => {
    if (item.menuItem.id !== menuItemId) return item;
    return {
      ...item,
      listSaveCount: Math.max(0, item.listSaveCount + delta),
    };
  };

  queryClient.setQueriesData<unknown>(
    { queryKey: FEED_QUERY_KEYS.all },
    (old: unknown) => {
      if (!old) return old;

      // Infinite query shape (activity, user, menu-item, business feeds)
      if (
        typeof old === 'object' &&
        'pages' in (old as any) &&
        Array.isArray((old as any).pages)
      ) {
        const data = old as InfiniteData<PaginatedFeedResponse>;
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map(updateItem),
          })),
        };
      }

      // Single FeedItem shape (check-in detail)
      if (typeof old === 'object' && 'checkIn' in (old as any)) {
        return updateItem(old as FeedItem);
      }

      return old;
    },
  );
}

/**
 * Hook for adding an item to a list
 *
 * On success, invalidates items, item status, and list detail (for count update).
 * Optimistically increments feed listSaveCount for the menu item.
 */
export function useAddListItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, data }: { listId: string; data: ListItemCreateInput }) =>
      listsApi.addListItem(listId, data),

    onMutate: async ({ data }) => {
      await queryClient.cancelQueries({ queryKey: FEED_QUERY_KEYS.all });
      const previousQueries = queryClient.getQueriesData<unknown>({ queryKey: FEED_QUERY_KEYS.all });
      optimisticFeedCountUpdate(queryClient, data.item_id, 1);
      return { previousQueries };
    },

    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.items(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.list(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: [...LISTS_QUERY_KEYS.all, 'item-status'] as const,
      });
    },

    onError: (error, _variables, context) => {
      console.error('[useAddListItem] Failed to add item to list:', error);
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries as [QueryKey, unknown][]) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook for removing an item from a list
 *
 * On success, invalidates items, item status, and list detail (for count update).
 * Optimistically decrements feed listSaveCount for the menu item.
 */
export function useRemoveListItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, itemId, itemType }: { listId: string; itemId: string; itemType: string }) =>
      listsApi.removeListItem(listId, itemId, itemType),

    onMutate: async ({ itemId }) => {
      await queryClient.cancelQueries({ queryKey: FEED_QUERY_KEYS.all });
      const previousQueries = queryClient.getQueriesData<unknown>({ queryKey: FEED_QUERY_KEYS.all });
      optimisticFeedCountUpdate(queryClient, itemId, -1);
      return { previousQueries };
    },

    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.items(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.list(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: [...LISTS_QUERY_KEYS.all, 'item-status'] as const,
      });
    },

    onError: (error, _variables, context) => {
      console.error('[useRemoveListItem] Failed to remove item from list:', error);
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries as [QueryKey, unknown][]) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook for updating a list item's note
 *
 * On success, invalidates the items query for the list.
 */
export function useUpdateListItemNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      listId,
      itemId,
      data,
    }: {
      listId: string;
      itemId: string;
      data: ListItemUpdateInput;
    }) => listsApi.updateListItemNote(listId, itemId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.items(variables.listId) });
    },
    onError: (error) => {
      console.error('[useUpdateListItemNote] Failed to update item note:', error);
    },
  });
}
